/** Pure unions for moving a guest's device progress onto an account. */

export interface MergeStreak {
  current: number;
  longest: number;
  lastDate: string | null;
}

export interface MergePractice<T extends { id: string } = { id: string }, E extends { id: string } = { id: string }> {
  bookmarks: string[];
  attempts: T[];
  exams: E[];
  streak: MergeStreak;
}

export interface MergeCard {
  id: string;
  savedAt: string;
}

function cleanStreak(streak: MergeStreak): MergeStreak {
  return {
    current: Math.max(0, Math.floor(streak.current) || 0),
    longest: Math.max(0, Math.floor(streak.longest) || 0),
    lastDate: streak.lastDate || null,
  };
}

function unionById<T extends { id: string }>(kept: T[], extra: T[]): T[] {
  const seen = new Set(kept.map((item) => item.id));
  return [...kept, ...extra.filter((item) => item.id && !seen.has(item.id))];
}

/** Same day keeps the higher count. It does not add the two streaks together. */
export function mergeStreak(device: MergeStreak, account: MergeStreak): MergeStreak {
  const left = cleanStreak(device);
  const right = cleanStreak(account);
  if (!left.lastDate) return right;
  if (!right.lastDate) return left;
  if (left.lastDate !== right.lastDate) return left.lastDate > right.lastDate ? left : right;
  const current = Math.max(left.current, right.current);
  return {
    current,
    longest: Math.max(left.longest, right.longest, current),
    lastDate: left.lastDate,
  };
}

export function mergePractice<T extends { id: string }, E extends { id: string }>(
  device: MergePractice<T, E>,
  account: MergePractice<T, E>,
): MergePractice<T, E> {
  return {
    bookmarks: [...new Set([...account.bookmarks, ...device.bookmarks].filter(Boolean))],
    attempts: unionById(account.attempts, device.attempts),
    exams: unionById(account.exams, device.exams),
    streak: mergeStreak(device.streak, account.streak),
  };
}

export function mergeSavedCards<T extends MergeCard>(device: T[], account: T[]): T[] {
  const map = new Map<string, T>();
  for (const card of account) {
    if (card.id) map.set(card.id, card);
  }
  for (const card of device) {
    if (!card.id) continue;
    const existing = map.get(card.id);
    if (!existing || String(card.savedAt || '') >= String(existing.savedAt || '')) map.set(card.id, card);
  }
  return [...map.values()];
}
